import { Build } from '@models/configurators/build';
import { ConfiguratorAction } from '@models/configurators/configurator-action';
import { ConfiguratorDI, Enumerable, Kbo } from '@models/configurators/configurator-di';
import { Container } from '@models/configurators/container';
import { CToken } from '@models/configurators/ctoken';
import { Field } from '@models/configurators/field';
import { Group } from '@models/configurators/group';
import { Hotspot } from '@models/configurators/hotspot';
import { IDummyNode } from '@models/configurators/kb-object';
import { KbObjectManager } from '@models/configurators/kb-object-manager';
import { OptionFilter } from '@models/configurators/option-filter';
import { CadOutput } from '@models/configurators/outputs/cad-output';
import { Output } from '@models/configurators/outputs/output';
import { Page } from '@models/configurators/page';
import { PartMaterial } from '@models/configurators/part-material';
import { PartOperation } from '@models/configurators/part-operation';
import { PartOperationDetail } from '@models/configurators/part-operation-detail';
import { PartRev } from '@models/configurators/partrev';
import { ReferencedConfigurator } from '@models/configurators/referenced-configurator';
import { RuleContainer } from '@models/configurators/rule-container';
import { UiObject } from '@models/configurators/ui-object';
import {
    IBooleanFieldRecording,
    IBuildType,
    IConfigurator,
    IConfiguredProduct,
    IDateFieldRecording,
    IFieldRecording,
    ILayoutSettingRecording,
    IMethodOfManufacture,
    INumberArrayFieldRecording,
    INumberFieldRecording,
    IPriceObject,
    IProduct,
    IStringArrayFieldRecording,
    IStringFieldRecording,
    IUploadFieldRecording,
} from '@models/entity-models';
import { eFieldType, eOptionFilterSource, eQueryMode, eRuleType, eValidationType, eViewerMode } from '@models/enums';
import { ConfiguratorFunction } from './configurator-function';
import { LayoutContent } from './layout-content';
import { NestedSet } from './nested-set';
import { Export } from './outputs/export';

@Kbo(CToken.Configurator)
export class Configurator extends Container implements IConfigurator {
    constructor(kom: KbObjectManager, obj?: any) {
        super(kom);

        kom.recurse(obj, this);
        this.$fieldsDirty = false; // reset fields dirty after the recurse
        this.$fieldsDirtyForSceneRules = true;
        this.$manager.addKbObject(this, undefined);

        this.$rulesNode = { id: this.id + "_rules", $type: "RulesNode", allowedChildren: () => [CToken.RuleContainer], getParent: () => this };
        this.$actionsNode = { id: this.id + "_actions", $type: "ActionsNode", allowedChildren: () => [CToken.ConfiguratorAction], getParent: () => this };
        this.$functionsNode = { id: this.id + "functions", $type: "FunctionsNode", allowedChildren: () => [CToken.ConfiguratorFunction], getParent: () => this };
        this.$optionFiltersNode = { id: this.id + "_filters", $type: "OptionFiltersNode", allowedChildren: () => [CToken.OptionFilter], getParent: () => this };
        this.$referencedConfiguratorsNode = { id: this.id + "_refConfigs", $type: "ReferencedConfiguratorsNode", allowedChildren: () => [CToken.ReferencedConfigurator], getParent: () => this };
        this.$buildsNode = { id: this.id + "_builds", $type: "BuildsNode", allowedChildren: () => [CToken.Build], getParent: () => this };
        this.$hotspotsNode = { id: this.id + "_hotspots", $type: "HotspotsNode", allowedChildren: () => [CToken.Hotspot], getParent: () => this };
    }

    // dummy nodes
    public $rulesNode: IDummyNode; // dummy object to act as the data item for the tree node
    public $actionsNode: IDummyNode; // dummy object to act as the data item for the tree node
    public $functionsNode: IDummyNode; // dummy object to act as the data item for the tree node
    public $optionFiltersNode: IDummyNode; // dummy object to act as the data item for the optionFilters node
    public $referencedConfiguratorsNode: IDummyNode;
    public $buildsNode: IDummyNode;
    public $hotspotsNode: IDummyNode;

    public idProduct: number;
    public idQuoteHeader: number;
    public entityType: string;
    public idLayout: number;

    private _layoutSettings: ILayoutSettingRecording[];
    @Enumerable()
    public get layoutSettings(): ILayoutSettingRecording[] {
        if (!this._layoutSettings) this._layoutSettings = [];
        return this._layoutSettings;
    }
    public set layoutSettings(val) {
        this._layoutSettings = val;
    }

    public allowedChildren(rootType: CToken) {
        return [CToken.Page];
    }

    // IProduct properties
    public shortDescription: string;
    public sku: string;
    public mfgPartNumber: string;
    public manufacturer: string;
    public unitOfMeasure: string;
    public price: number;
    public cost: number;
    public maxDiscountPercentage: number;
    public maxQty: number;
    public minQty: number;
    public allowFractionalQty: boolean;
    public relatedProductsQuery: string;
    public score: number;
    public active: boolean;
    public priceObject: IPriceObject;

    public inheritCategoryPermissions: boolean;

    public v: number;
    public snapVersion: number;
    public validationTiming: string;
    public allowSaveWithErrors: boolean;
    public layout: string;
    public tabStyle: string;
    public tabIconSize: number;
    public headerColor: string;
    public viewerMediaPath: string;
    public autoValidation: boolean = true;
    public showPrice: boolean;
    public showNavigation: boolean;
    public showSubmitButton: boolean;
    public submitButtonText: string;
    public generateQrCodeText: string;
    public submitAndStayButtonText: string;
    public showMoveAnimation: boolean;
    public showTabs: boolean;
    public showTabLabels: boolean;
    public showActionBar: boolean;
    public hasPricingRule: boolean;
    public hasQuoteRule: boolean;

    public nestedButtonText: string;
    public allowUserToRemove: boolean;

    public alwaysChild: boolean;
    public alwaysChildOf: number;

    public methodOfManufacture: IMethodOfManufacture;

    /** when a child of a nested set, will be filled with the element in this configurator whose UI will be used in the parent */
    public $nestedSetElement: UiObject;
    /**
     * should only be true when the configurator is running on the client
     */
    public $running: boolean;

    /**
     * if this configurator is a nested and was added by the end user, then stores the id of the user that added it
     */
    public addedByUser: number;
    /**
     * whether the fields in this configurator have been modified since the last rule cycle
     */
    public $fieldsDirty: boolean;

    /**
     * whether the fields in this configurator have been modified since the last scene rules cycle
     */
    public $fieldsDirtyForSceneRules: boolean;

    /**
     * freezes fields so they will not recalculate their value when calling field.value get() unless you
     * explicitly tell them to.  Do not use unles you are fully aware of what you are doing.
     */
    public $freezeFieldValueCalculation: boolean;
    /**
     *  Whether a value change in direct child configurators cause a rule cycle in this configurator.
     *  Calculated by the configurator admin.
     */
    public bubbleRules: boolean;
    /**
     * Holds the rootscene's id as a string containing "rootconfig.parentconfig.name/rootconfig.name"
     * This is used to key the values in sceneViewerDb
     */
    public nestedPath: string | number;
    /**
     * if this is a scene configurator, holds the uuid of the clara scene
     */
    public claraId: string;
    /**
     *  a unique id for tracking nested scenes.  Only filled in if this configurator's
     * scene has been nested into a higher level scene
     */
    public $nestedSceneId: string;
    /**
     * a map of old scene id's to new scene id's.  This is filled in when this is a nested scene configurator
     */
    public $sceneIdMap: {};
    /**
     * Used by sceneService as a placeholder for the scene configurator that correlates
     * to this cpq configurator (used for nested scene rules)
     */
    public $sceneConfigurator: Configurator;
    /**
     * When using kb3d, will hold a reference to the scene node that corresponds with this configurator
     */
    public $sceneNode: any;
    /**
     * Used by sceneService to determine whether this configurator should bubble up it's scene hotspots
     * when used as a nested configurator
     */
    public $nestHotspots: boolean;
    /**
     * When this configurator is a scene configurator, then uiConfigurator will be filled in
     * with it's corresponding full CPQ configurator
     */
    public $uiConfigurator: Configurator;
    /**
     * whether mouse over effects are enabled in the scene viewer
     */
    public mouseOverEffects: boolean = true;
    /**
     * the color that will be used for the emissive property of the mouse over materials
     */
    public mouseOverColor: string = '#131313';
    /**
     * when true, images in the scene will be prefetched to speed up performance of switching images/materials
     */
    public prefetchImages: boolean = true;
    /**
     * stores parameters passed into the configurator through the URL
     */
    parameters?: { [key: string]: any };
    /*
     * One of the properties for integrating with Kinetic
     */
    usePartSelector?: boolean;
    /**
     * convienence property set by the configurator view to determine if the loaded rule had been
     * run for this configurator to prevent "double loading"
     */
    public $ranLoadedRule: boolean = false;

    public convertToKb3d: boolean;
    public allowOrbit: boolean;
    public allowPan: boolean;
    public allowZoom: boolean;
    public constrainCameraToRadius: boolean;
    public cameraRadius: number;
    public pagePosition: string;
    public tabPosition: string;
    public pageWidth: number;
    public pageWidthUnits: string;

    private _viewerMode: string = eViewerMode.none;
    @Enumerable()
    public get viewerMode(): string {
        return this._viewerMode;
    }
    public set viewerMode(val) {
        this.setProperty('viewerMode', val);
    }

    private _idScene: number;
    @Enumerable()
    public get idScene(): number {
        return this._idScene;
    }
    public set idScene(val) {
        this.setProperty('idScene', val);
    }

    public clearMemoryWhenSwitchingScenes: boolean;
    public sceneToolbarPosition: string;
    public nestedSceneClickToSelect: boolean;

    private _pages: Page[];
    @Enumerable()
    public get pages(): Page[] {
        if (!this._pages) this._pages = [];
        return this._pages;
    }
    public set pages(val) {
        this._pages = val;
    }

    private _builds: Build[];
    @Enumerable()
    public get builds(): Build[] {
        if (!this._builds) this._builds = [];
        return this._builds;
    }
    public set builds(val) {
        this._builds = val;
    }

    private _optionFilters: OptionFilter[];
    @Enumerable()
    public get optionFilters(): OptionFilter[] {
        if (!this._optionFilters) this._optionFilters = [];
        return this._optionFilters;
    }
    public set optionFilters(val) {
        this._optionFilters = val;
    }

    private _configurators: Configurator[];
    @Enumerable()
    public get configurators(): Configurator[] {
        if (!this._configurators) this._configurators = [];
        return this._configurators;
    }
    public set configurators(val) {
        this._configurators = val;
    }

    private _referencedConfigurators: ReferencedConfigurator[];
    @Enumerable()
    public get referencedConfigurators(): ReferencedConfigurator[] {
        if (!this._referencedConfigurators) this._referencedConfigurators = [];
        return this._referencedConfigurators;
    }
    public set referencedConfigurators(val) {
        this._referencedConfigurators = val;
    }

    private _referencedScenes: number[];
    @Enumerable()
    public get referencedScenes(): number[] {
        if (!this._referencedScenes) this._referencedScenes = [];
        return this._referencedScenes;
    }
    public set referencedScenes(val) {
        this._referencedScenes = val;
    }

    private _actions: ConfiguratorAction[];
    @Enumerable()
    public get actions(): ConfiguratorAction[] {
        if (!this._actions) this._actions = [];
        return this._actions;
    }
    public set actions(val) {
        this._actions = val;
    }

    private _functions: ConfiguratorFunction[];
    @Enumerable()
    public get functions(): ConfiguratorFunction[] {
        if (!this._functions) this._functions = [];
        return this._functions;
    }
    public set functions(val) {
        this._functions = val;
    }

    private _hotspots: Hotspot[];
    @Enumerable()
    public get hotspots(): Hotspot[] {
        if (!this._hotspots) this._hotspots = [];
        return this._hotspots;
    }
    public set hotspots(val) {
        this._hotspots = val;
    }

    private _ruleContainers: RuleContainer[];
    @Enumerable()
    public get ruleContainers(): RuleContainer[] {
        if (!this._ruleContainers) this._ruleContainers = [];
        return this._ruleContainers;
    }
    public set ruleContainers(val) {
        this._ruleContainers = val;
    }

    private _layoutContents: LayoutContent[];
    @Enumerable()
    public get layoutContents(): LayoutContent[] {
        if (!this._layoutContents) this._layoutContents = [];
        return this._layoutContents;
    }
    public set layoutContents(val) {
        this._layoutContents = val;
    }

    public isNested() {
        return !!this.$parent;
    }

    public allPages(): Page[] {
        return this.getChildrenOfType<Page>(CToken.Page);
    }

    public allGroups(): Group[] {
        return this.getChildrenOfType<Group>(CToken.Group);
    }

    /**
     *
     * @param token Gets all the UI children of the given types.
     * @param allChildren
     */
    public getChildrenOfType<T extends UiObject>(...tokens: CToken[]): T[] {
        return this.filterChildren<T>(
            tokens,
            this.children.concat(this.pages).concat(this.layoutContents.selectMany(lc => lc.children)) //get layout contents children, but not the layout contents themselves
        );
    }

    public allRuleContainers(): RuleContainer[] {
        return this.ruleContainers
            .concat(this.actions)
            .concat(this.optionFilters)
            .concat(this.getFields())
            .concat(this.getOutputs());
    }

    public addPage(): Page {
        let page = new Page(this.$manager);
        this.$manager.addKbObject(page, this);
        this.pages.push(page);
        return page;
    }

    public removePage(page: Page) {
        this.$manager.removeKbObject(page);
        this.pages.remove(page);
    }

    public addAction(): ConfiguratorAction {
        let action = new ConfiguratorAction(this.$manager);
        this.$manager.addKbObject(action, this);
        this.actions.push(action);
        return action;
    }

    public removeAction(action: ConfiguratorAction) {
        this.$manager.removeKbObject(action);
        this.actions.remove(action);
    }

    public addFunction(): ConfiguratorFunction {
        let f = new ConfiguratorFunction(this.$manager);
        f.name = 'Function';
        this.$manager.addKbObject(f, this);
        this.functions.push(f);
        return f;
    }

    public removeFunction(f: ConfiguratorFunction) {
        this.$manager.removeKbObject(f);
        this.functions.remove(f);
    }

    public addOptionFilter(): OptionFilter {
        let st = new OptionFilter(this.$manager);
        this.$manager.addKbObject(st, this);
        this.optionFilters.push(st);
        return st;
    }

    public removeOptionFilter(optionFilter: OptionFilter) {
        this.$manager.removeKbObject(optionFilter);
        this.optionFilters.remove(optionFilter);
    }

    public removeRuleContainer(rc: RuleContainer) {
        this.$manager.removeKbObject(rc);
        this.ruleContainers.remove(rc);
    }

    public addHotspot(): Hotspot {
        let ann = new Hotspot(this.$manager);
        this.$manager.addKbObject(ann, this);
        this.hotspots.push(ann);
        return ann;
    }

    public removeHotspot(ann: Hotspot) {
        this.$manager.removeKbObject(ann);
        this.hotspots.remove(ann);
    }

    public addBuild(buildType: IBuildType): Build {
        let build: Build;
        // we should only have one buildtype in a configurator
        let exists = this.builds.some(b => b.idBuildType == buildType.id);
        if (!exists) {
            build = new Build(this.$manager);
            build.idBuildType = buildType.id;
            build.name = buildType.name;
            this.$manager.addKbObject(build, this);
            this.builds.push(build);
        }

        return build;
    }

    public removeBuild(build: Build) {
        this.$manager.removeKbObject(build);
        this.builds.remove(build);
    }

    public addReferencedConfigurator(idProduct: number) {
        let rc = this.referencedConfigurators.find(rc => rc.idProduct == idProduct);
        if (!rc) {
            rc = new ReferencedConfigurator(this.$manager);
            rc.idProduct = idProduct;
            this.referencedConfigurators.push(rc);
            this.$manager.addKbObject(rc, this);
        }

        return rc;
    }

    public removeReferencedConfigurator(rc: ReferencedConfigurator) {
        this.$manager.removeKbObject(rc);
        this.referencedConfigurators.remove(rc);
    }

    public addReferencedScene(idScene: number) {
        if (!this.referencedScenes.find(rs => rs == idScene)) {
            this.referencedScenes.push(idScene);
        }
    }

    public removeReferencedScene(rs: number) {
        this.referencedScenes.remove(rs);
    }

    public getNestedConfigurators(): Configurator[] {
        let result: Configurator[] = [];
        this.configurators.forEach(n => result.push(n));
        this.allPages().forEach(p => {
            p.configurators.forEach(n => {
                result.push(n);
            });
        });
        this.getChildrenOfType<NestedSet>(CToken.NestedSet).forEach(ns => {
            ns.configurators.forEach(n => {
                result.push(n);
            });
        });

        return result;
    }

    /**
     * get's all configurators, including children of children, etc.
     */
    public getAllConfigurators(): Configurator[] {
        let allNested = [];
        let addToNested = (config: Configurator) => {
            let nestedConfigs = config.getNestedConfigurators();
            allNested.pushArray(nestedConfigs);
            nestedConfigs.forEach(n => addToNested(n));
        };
        addToNested(this);
        return allNested;
    }

    public hasRule(ruleType: string): boolean {
        let rc = this.ruleContainers.find(rc => {
            return rc.ruleType.isEqual(ruleType);
        });
        return rc != null && rc.js && rc.js.length > 0;
    }
    /**
     * gets a concatinated javascript rule for the specified rule type
     */
    public getRuleJs(ruleType: string): string {
        let rc = this.ruleContainers.find(rc => {
            return rc.ruleType.isEqual(ruleType);
        });
        return rc ? this.getRuleJsFromContainer(rc) : '';
    }

    public getRuleJsFromContainer(ruleContainer: RuleContainer): string {
        // global rule should always be included
        let globalRc = this.ruleContainers.find(rc => rc.ruleType.isEqual(eRuleType.global));
        let globalJs = globalRc ? globalRc.js + '\n\n' : '';
        return globalJs + ruleContainer.js;
    }

    public preValidate() {
        this.getFields().forEach(field => field.preValidate());
    }

    public isValid(): boolean {
        let v = true;

        for (let page of this.pages) {
            if (!page.isValid()) {
                v = false;
            }
        }
        for (let config of this.configurators) {
            if (!config.isValid()) {
                v = false;
            }
        }

        this.$valid = v;
        return this.$valid;
    }

    /**
     * Sets field values based on an object whose properties are field names and values are the field values to set
     */
    public setFields(fields: {}) {
        for (let prop in fields) {
            if (fields.hasOwnProperty(prop)) {
                let field = this.$manager.getByIdOrName<Field>(prop, false);
                if (field) {
                    field.value = fields[prop];
                }
            }
        }
    }

    public getOutputs(predicate?: (output: Output) => boolean): Output[] {
        let result = [];

        let process = o => {
            if (predicate) {
                if (predicate(o)) {
                    result.push(o);
                }
            } else {
                result.push(o);
            }
        };
        this.builds.forEach(b => {
            b.outputs.forEach(o => {
                process(o);
                if (ConfiguratorDI.instanceOf<CadOutput>(o, CToken.CadOutput)) {
                    o.drawings.forEach(process);
                }
            });
        });

        return result;
    }

    public getExports(predicate?: (exp: Export) => boolean): Export[] {
        let exports = [];
        let outputs = this.getOutputs();
        for (let o of outputs) {
            for (let exp of o.exports) {
                if ((predicate && predicate(exp)) || !predicate) {
                    exports.push(exp);
                }
            }
        }
        return exports;
    }

    public getFields(predicate?: (field: Field) => boolean): Field[] {
        let fields = this.getChildrenOfType<Field>(CToken.Field);
        return predicate ? fields.filter(f => predicate(f)) : fields;
    }

    public getGroups(predicate?: (group: Group) => boolean): Group[] {
        let groups = this.getChildrenOfType<Group>(CToken.Group);
        return predicate ? groups.filter(g => predicate(g)) : groups;
    }

    public getFieldsObject(): {} {
        let fields = this.getFields();
        let o = {};
        fields.forEach(field => {
            o[field.name] = field.value;
        });
        return o;
    }

    public runConfiguredProduct(configProduct: IConfiguredProduct, productDb: { [idProduct: number]: IProduct }) {
        if (configProduct.fields) {
            configProduct.fields.forEach(fieldRec => {
                let field = this.$manager.get<Field>(fieldRec.id, false);
                if (!field) field = this.$manager.getByName<Field>(fieldRec.name, false);
                if (field && field.$type == CToken.Field.key) {
                    if (field.type == eFieldType.text || field.type == eFieldType.color) {
                        field.value = (fieldRec as IStringFieldRecording).stringValue;
                    } else if (field.type == eFieldType.number) {
                        field.value = (fieldRec as INumberFieldRecording).numberValue;
                    } else if (field.type == eFieldType.boolean) {
                        field.value = (fieldRec as IBooleanFieldRecording).booleanValue;
                    } else if (field.type == eFieldType.date) {
                        field.value = (fieldRec as IDateFieldRecording).dateValue;
                    } else if (field.type == eFieldType.upload) {
                        field.value = (fieldRec as IUploadFieldRecording).uploadValue;
                    } else if (field.type == eFieldType.textArray || field.type == eFieldType.colorArray) {
                        field.value = (fieldRec as IStringArrayFieldRecording).stringArrayValue;
                    } else if (field.type == eFieldType.numberArray) {
                        field.value = (fieldRec as INumberArrayFieldRecording).numberArrayValue;
                    } else {
                        throw new Error('not implemented');
                    }
                    // } else if (field.type == eFieldType.date) {
                    //    field.value = (<IDateFieldRecording>fieldRec).dateValue;
                    // } else if (field.type == eFieldType.object) {
                    //    field.value = (<IObjectFieldRecording>fieldRec).objectValue;
                    // } else if (field.type == eFieldType.array) {
                    //    throw "not implemented";
                    // }
                }
            });
        }
        if (configProduct.configurators) {
            configProduct.configurators.forEach(nestRec => {
                let parent = this.$manager.get<Page | Configurator | NestedSet>(nestRec.idParent, false) || this;
                let nested = parent.configurators.find(c => c.name.isEqual(nestRec.name));
                if (!nested) {
                    nested = new Configurator(new KbObjectManager(), productDb[nestRec.idProduct].configurator);
                    nested.$running = this.$running;
                    nested.name = nestRec.name;
                    parent.configurators.push(nested);
                    this.$manager.addKbObject(nested, parent);
                    nested.addedByUser = nestRec.addedByUser;
                    nested.allowUserToRemove = nestRec.allowUserToRemove;
                }

                nested.runConfiguredProduct(nestRec, productDb);
            });
        }
    }

    /**
     * gets the configured product of the configurator.  WithDesiredValues should only be
     * set to true when sending to the server to run option filters.  It sends the desiredValue of the fields
     * as opposed to the calculated value based on options.
     * @param withDesiredValues
     */
    public getConfiguredProduct(withDesiredValues = false): IConfiguredProduct {
        let getFieldValue = (field: Field) => {
            if (withDesiredValues && field.idOptionFilter && field.canJoinOptionFilter()) {
                return field.desiredValue;
            } else {
                return field.value;
            }
        };

        let configProduct = {
            configurators: [],
            fields: [],
            name: this.name,
            entityType: this.entityType,
            idProduct: this.idProduct,
            idQuoteHeader: this.idQuoteHeader,
            addedByUser: this.addedByUser,
            allowUserToRemove: this.allowUserToRemove,
            idParent: this.$parent && this.$parent.id,
            idScene: this.idScene,
            validationMessages: [],
            parameters: this.parameters,
        } as IConfiguredProduct;

        this.getFields().forEach(field => {
            let fieldRec = {
                id: field.id,
                name: field.name,
            } as IFieldRecording;
            configProduct.fields.push(fieldRec);
            if (field.type == eFieldType.text || field.type == eFieldType.color) {
                (fieldRec as IStringFieldRecording).stringValue = getFieldValue(field);
            } else if (field.type == eFieldType.number) {
                (fieldRec as INumberFieldRecording).numberValue = getFieldValue(field);
            } else if (field.type == eFieldType.boolean) {
                (fieldRec as IBooleanFieldRecording).booleanValue = getFieldValue(field);
            } else if (field.type == eFieldType.date) {
                (fieldRec as IDateFieldRecording).dateValue = getFieldValue(field);
            } else if (field.type == eFieldType.upload) {
                (fieldRec as IUploadFieldRecording).uploadValue = getFieldValue(field);
            } else if (field.type == eFieldType.textArray || field.type == eFieldType.colorArray) {
                (fieldRec as IStringArrayFieldRecording).stringArrayValue = getFieldValue(field);
            } else if (field.type == eFieldType.numberArray) {
                (fieldRec as INumberArrayFieldRecording).numberArrayValue = getFieldValue(field);
            } else {
                throw new Error('not implemented');
            }
            field.validationErrors.forEach(err => {
                configProduct.validationMessages.push({
                    messageType: eValidationType.error,
                    object: { id: field.id, name: field.$label },
                    message: err.message,
                });
            });
            field.warnings.forEach(warn => {
                configProduct.validationMessages.push({
                    messageType: eValidationType.warning,
                    object: { id: field.id, name: field.$label },
                    message: warn.message,
                });
            });
            // } else if (field.type == eFieldType.date) {
            //    (<IDateFieldRecording>fieldRec).dateValue = field.value;
            // } else if (field.type == eFieldType.object) {
            //    (<IObjectFieldRecording>fieldRec).objectValue = field.value;
            // } else if (field.type == eFieldType.array) {
            //    throw "not implemented";
            // }
        });

        this.getNestedConfigurators().forEach(nested => {
            let configuredNested = nested.getConfiguredProduct(withDesiredValues);
            configProduct.configurators.push(configuredNested);
        });

        return configProduct;
    }

    public filtersShouldRunOnClient() {
        return !(
            this.optionFilters.some(o => o.source == eOptionFilterSource.database) ||
            this.optionFilters.some(o => o.source == eOptionFilterSource.safeFunction) ||
            this.optionFilters.some(o => o.source == eOptionFilterSource.query && o.queryMode == eQueryMode.database)
        );
    }

    public getPartMaterial(mtlPartNum: string, methodOfManufacture: PartRev = null): PartMaterial {
        let result: PartMaterial = null;
        let recurse = (material: PartMaterial) => {
            if (result) return;
            if (material.mtlPartNum == mtlPartNum) result = material;
            if (material.materials) {
                for (let i in material.materials) {
                    recurse(material.materials[i]);
                }
            }
        };
        if (!methodOfManufacture) methodOfManufacture = <PartRev>this.methodOfManufacture.rootPartRev;
        if (methodOfManufacture.partNum == mtlPartNum || (<any>methodOfManufacture).mtlPartNum == mtlPartNum)
            return <any>methodOfManufacture;
        for (let i in methodOfManufacture.materials) {
            recurse(methodOfManufacture.materials[i]);
        }
        return result;
    }

    public getPartOperation(opCode: string, methodOfManufacture: PartRev = null): PartOperation {
        let result: PartOperation = null;
        let recurse = (material: PartMaterial) => {
            if (result) return;
            for (let i in material.operations) {
                if (material.operations[i].opCode == opCode) result = material.operations[i];
            }
            if (material.materials) {
                for (let i in material.materials) {
                    recurse(material.materials[i]);
                }
            }
        };
        if (!methodOfManufacture) methodOfManufacture = <PartRev>this.methodOfManufacture.rootPartRev;

        for (let i in methodOfManufacture.operations) {
            if (methodOfManufacture.operations[i].opCode == opCode) result = methodOfManufacture.operations[i];
        }
        for (let i in methodOfManufacture.materials) {
            recurse(methodOfManufacture.materials[i]);
        }
        return result;
    }

    public getPartOperationDetail(id: string, methodOfManufacture: PartRev = null): PartOperationDetail {
        let result: PartOperationDetail = null;
        let recurse = (material: PartMaterial) => {
            if (result) return;
            for (let i in material.operations) {
                for (let j in material.operations[i]) {
                    let opDtl = material.operations[i].operationDetails[j];
                    if (opDtl.resourceID == id || opDtl.resourceGrpID == id) result = opDtl;
                }
            }
            if (material.materials) {
                for (let i in material.materials) {
                    recurse(material.materials[i]);
                }
            }
        };
        if (!methodOfManufacture) methodOfManufacture = <PartRev>this.methodOfManufacture.rootPartRev;

        for (let i in methodOfManufacture.operations) {
            for (let j in methodOfManufacture.operations[i].operationDetails) {
                let opDtl = methodOfManufacture.operations[i].operationDetails[j];
                if (opDtl.resourceID == id || opDtl.resourceGrpID == id) result = opDtl;
            }
        }
        for (let i in methodOfManufacture.materials) {
            recurse(methodOfManufacture.materials[i]);
        }
        return result;
    }
}
